/**
 * Admin Moderation Page
 *
 * Review and moderate pending/flagged inspector notes.
 * Part of Phase 2: Validation System
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { AdminPageHeader, AdminCard, AdminStatCard } from '@/components/admin/AdminShell';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  MessageSquare,
  Flag,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  Shield,
  Users,
  MapPin,
  Eye,
  ThumbsUp,
  ThumbsDown,
  Zap,
} from 'lucide-react';

interface ModerationQueueItem {
  id: string;
  content: string;
  sanitized_content: string | null;
  note_type: string;
  evidence_level: string;
  trade: string | null;
  status: string;
  validation_state: string;
  quality_score: number | null;
  auto_moderated: boolean;
  moderation_reason: string | null;
  flag_count: number;
  created_at: string;
  created_by_org_id: string;
  jurisdiction_name: string | null;
  county_name: string | null;
  contributor_reputation: number | null;
  contributor_total_notes: number | null;
  contributor_accuracy: number | null;
}

interface ModerationStats {
  pending_count: number;
  flagged_count: number;
  approved_today: number;
  rejected_today: number;
  auto_approved_rate: number;
}

export default function AdminModeration() {
  const { adminUser } = useAdminAuth();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'pending' | 'flagged'>('pending');
  const [selectedNote, setSelectedNote] = useState<ModerationQueueItem | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectDialog, setShowRejectDialog] = useState(false);

  // Fetch moderation stats
  const { data: stats } = useQuery({
    queryKey: ['moderation-stats'],
    queryFn: async (): Promise<ModerationStats> => {
      if (!supabase) return {
        pending_count: 0,
        flagged_count: 0,
        approved_today: 0,
        rejected_today: 0,
        auto_approved_rate: 0,
      };

      const { data, error } = await supabase.rpc('get_moderation_stats');
      if (error) throw error;
      return data?.[0] || {
        pending_count: 0,
        flagged_count: 0,
        approved_today: 0,
        rejected_today: 0,
        auto_approved_rate: 0,
      };
    },
    staleTime: 1000 * 30, // 30 seconds
  });

  // Fetch moderation queue
  const { data: queue = [], isLoading } = useQuery({
    queryKey: ['moderation-queue', activeTab],
    queryFn: async (): Promise<ModerationQueueItem[]> => {
      if (!supabase) return [];

      const { data, error } = await supabase
        .from('moderation_queue')
        .select('*')
        .eq('status', activeTab)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return (data || []) as ModerationQueueItem[];
    },
    staleTime: 1000 * 10, // 10 seconds
  });

  // Approve mutation
  const approveMutation = useMutation({
    mutationFn: async (noteId: string) => {
      if (!supabase || !adminUser) throw new Error('Not authenticated');

      const { data, error } = await supabase.rpc('approve_note', {
        p_note_id: noteId,
        p_moderator_id: adminUser.id,
        p_reason: 'Manual approval by admin',
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['moderation-queue'] });
      queryClient.invalidateQueries({ queryKey: ['moderation-stats'] });
      setSelectedNote(null);
    },
  });

  // Reject mutation
  const rejectMutation = useMutation({
    mutationFn: async ({ noteId, reason }: { noteId: string; reason: string }) => {
      if (!supabase || !adminUser) throw new Error('Not authenticated');

      const { data, error } = await supabase.rpc('reject_note', {
        p_note_id: noteId,
        p_moderator_id: adminUser.id,
        p_reason: reason || 'Rejected by admin',
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['moderation-queue'] });
      queryClient.invalidateQueries({ queryKey: ['moderation-stats'] });
      setSelectedNote(null);
      setShowRejectDialog(false);
      setRejectReason('');
    },
  });

  // Flag mutation (move back to flagged queue)
  const flagMutation = useMutation({
    mutationFn: async ({ noteId, reason }: { noteId: string; reason: string }) => {
      if (!supabase || !adminUser) throw new Error('Not authenticated');

      const { data, error } = await supabase.rpc('flag_note_for_review', {
        p_note_id: noteId,
        p_moderator_id: adminUser.id,
        p_reason: reason || 'Flagged for additional review',
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['moderation-queue'] });
      queryClient.invalidateQueries({ queryKey: ['moderation-stats'] });
      setSelectedNote(null);
    },
  });

  const handleApprove = (noteId: string) => {
    approveMutation.mutate(noteId);
  };

  const handleReject = () => {
    if (selectedNote) {
      rejectMutation.mutate({ noteId: selectedNote.id, reason: rejectReason });
    }
  };

  const handleFlag = (noteId: string) => {
    flagMutation.mutate({ noteId, reason: 'Needs additional review' });
  };

  const getNoteTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      tip: 'Tip',
      pattern: 'Pattern',
      fail_reason: 'Fail Reason',
      preference: 'Preference',
      schedule: 'Scheduling',
    };
    return labels[type] || type;
  };

  const getQualityColor = (score: number | null) => {
    if (score === null) return 'text-gray-500';
    if (score >= 70) return 'text-green-400';
    if (score >= 50) return 'text-yellow-400';
    return 'text-red-400';
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <div>
      <AdminPageHeader
        title="Content Moderation"
        description="Review and moderate inspector notes submissions"
      />

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <AdminStatCard
          label="Pending Review"
          value={stats?.pending_count || 0}
          change="Awaiting moderation"
          changeType="neutral"
          icon={<Clock className="h-5 w-5" />}
        />
        <AdminStatCard
          label="Flagged"
          value={stats?.flagged_count || 0}
          change="Needs attention"
          changeType={stats?.flagged_count ? 'negative' : 'neutral'}
          icon={<Flag className="h-5 w-5" />}
        />
        <AdminStatCard
          label="Approved Today"
          value={stats?.approved_today || 0}
          change="Manual approvals"
          changeType="positive"
          icon={<CheckCircle className="h-5 w-5" />}
        />
        <AdminStatCard
          label="Auto-Approve Rate"
          value={`${((stats?.auto_approved_rate || 0) * 100).toFixed(0)}%`}
          change="Last 7 days"
          changeType="neutral"
          icon={<Zap className="h-5 w-5" />}
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'pending' | 'flagged')}>
        <TabsList className="bg-[hsl(220,15%,15%)]">
          <TabsTrigger value="pending" className="data-[state=active]:bg-[hsl(220,70%,50%)]">
            <Clock className="h-4 w-4 mr-2" />
            Pending ({stats?.pending_count || 0})
          </TabsTrigger>
          <TabsTrigger value="flagged" className="data-[state=active]:bg-[hsl(220,70%,50%)]">
            <Flag className="h-4 w-4 mr-2" />
            Flagged ({stats?.flagged_count || 0})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-4">
          <ModerationQueue
            items={queue}
            isLoading={isLoading}
            onApprove={handleApprove}
            onReject={(note) => {
              setSelectedNote(note);
              setShowRejectDialog(true);
            }}
            onFlag={handleFlag}
            onView={setSelectedNote}
            getNoteTypeLabel={getNoteTypeLabel}
            getQualityColor={getQualityColor}
            formatDate={formatDate}
          />
        </TabsContent>

        <TabsContent value="flagged" className="mt-4">
          <ModerationQueue
            items={queue}
            isLoading={isLoading}
            onApprove={handleApprove}
            onReject={(note) => {
              setSelectedNote(note);
              setShowRejectDialog(true);
            }}
            onFlag={handleFlag}
            onView={setSelectedNote}
            getNoteTypeLabel={getNoteTypeLabel}
            getQualityColor={getQualityColor}
            formatDate={formatDate}
            isFlagged
          />
        </TabsContent>
      </Tabs>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent className="bg-[hsl(220,20%,10%)] border-[hsl(220,15%,20%)]">
          <DialogHeader>
            <DialogTitle className="text-white">Reject Note</DialogTitle>
            <DialogDescription className="text-gray-400">
              Provide a reason for rejecting this note. This will be logged for audit purposes.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reason for rejection..."
              className="bg-[hsl(220,15%,15%)] border-[hsl(220,15%,25%)] text-white"
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowRejectDialog(false)}
              className="border-[hsl(220,15%,25%)] text-gray-300"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={rejectMutation.isPending}
            >
              {rejectMutation.isPending ? 'Rejecting...' : 'Reject Note'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!selectedNote && !showRejectDialog} onOpenChange={() => setSelectedNote(null)}>
        <DialogContent className="bg-[hsl(220,20%,10%)] border-[hsl(220,15%,20%)] max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">Review Note</DialogTitle>
          </DialogHeader>
          {selectedNote && (
            <div className="space-y-4">
              {/* Badges */}
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" className="text-gray-300 border-gray-600">
                  {getNoteTypeLabel(selectedNote.note_type)}
                </Badge>
                {selectedNote.trade && (
                  <Badge variant="secondary" className="capitalize">
                    {selectedNote.trade}
                  </Badge>
                )}
                <Badge variant="outline" className="text-gray-300 border-gray-600">
                  {selectedNote.evidence_level}
                </Badge>
                {selectedNote.auto_moderated && (
                  <Badge className="bg-purple-600">Auto-Moderated</Badge>
                )}
              </div>

              {/* Content */}
              <AdminCard>
                <p className="text-white">{selectedNote.content}</p>
                {selectedNote.sanitized_content && selectedNote.sanitized_content !== selectedNote.content && (
                  <div className="mt-3 pt-3 border-t border-[hsl(220,15%,20%)]">
                    <p className="text-xs text-amber-400 mb-1">Sanitized version (PII removed):</p>
                    <p className="text-gray-300 text-sm">{selectedNote.sanitized_content}</p>
                  </div>
                )}
              </AdminCard>

              {/* Metadata */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Location</p>
                  <p className="text-white flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {selectedNote.jurisdiction_name || 'Unknown'}
                    {selectedNote.county_name && `, ${selectedNote.county_name}`}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Quality Score</p>
                  <p className={`font-medium ${getQualityColor(selectedNote.quality_score)}`}>
                    {selectedNote.quality_score !== null ? `${selectedNote.quality_score}/100` : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Contributor</p>
                  <p className="text-white flex items-center gap-1">
                    <Users className="h-3 w-3" />
                    Rep: {selectedNote.contributor_reputation?.toFixed(0) || '50'}
                    {selectedNote.contributor_total_notes && ` (${selectedNote.contributor_total_notes} notes)`}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Submitted</p>
                  <p className="text-white">{formatDate(selectedNote.created_at)}</p>
                </div>
              </div>

              {/* Moderation Reason */}
              {selectedNote.moderation_reason && (
                <AdminCard className="bg-[hsl(220,15%,12%)]">
                  <p className="text-xs text-gray-500 mb-1">Auto-moderation result:</p>
                  <p className="text-gray-300 text-sm">{selectedNote.moderation_reason}</p>
                </AdminCard>
              )}

              {/* Flag count */}
              {selectedNote.flag_count > 0 && (
                <div className="flex items-center gap-2 text-amber-400">
                  <Flag className="h-4 w-4" />
                  <span>{selectedNote.flag_count} user flag(s)</span>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setSelectedNote(null)}
              className="border-[hsl(220,15%,25%)] text-gray-300"
            >
              Close
            </Button>
            {selectedNote?.status === 'pending' && (
              <Button
                variant="outline"
                onClick={() => handleFlag(selectedNote.id)}
                className="border-amber-600 text-amber-400 hover:bg-amber-600/20"
              >
                <Flag className="h-4 w-4 mr-2" />
                Flag for Review
              </Button>
            )}
            <Button
              variant="destructive"
              onClick={() => {
                setShowRejectDialog(true);
              }}
            >
              <XCircle className="h-4 w-4 mr-2" />
              Reject
            </Button>
            <Button
              onClick={() => selectedNote && handleApprove(selectedNote.id)}
              disabled={approveMutation.isPending}
              className="bg-green-600 hover:bg-green-700"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              {approveMutation.isPending ? 'Approving...' : 'Approve'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface ModerationQueueProps {
  items: ModerationQueueItem[];
  isLoading: boolean;
  onApprove: (noteId: string) => void;
  onReject: (note: ModerationQueueItem) => void;
  onFlag: (noteId: string) => void;
  onView: (note: ModerationQueueItem) => void;
  getNoteTypeLabel: (type: string) => string;
  getQualityColor: (score: number | null) => string;
  formatDate: (dateStr: string) => string;
  isFlagged?: boolean;
}

function ModerationQueue({
  items,
  isLoading,
  onApprove,
  onReject,
  onFlag,
  onView,
  getNoteTypeLabel,
  getQualityColor,
  formatDate,
  isFlagged,
}: ModerationQueueProps) {
  if (isLoading) {
    return (
      <AdminCard>
        <div className="text-center py-8 text-gray-500">Loading...</div>
      </AdminCard>
    );
  }

  if (items.length === 0) {
    return (
      <AdminCard>
        <div className="text-center py-12">
          <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">
            {isFlagged ? 'No Flagged Notes' : 'Queue Empty'}
          </h3>
          <p className="text-gray-400">
            {isFlagged
              ? 'There are no flagged notes requiring review.'
              : 'All notes have been reviewed. Great job!'}
          </p>
        </div>
      </AdminCard>
    );
  }

  return (
    <div className="space-y-3">
      {items.map((note) => (
        <AdminCard key={note.id} className="hover:bg-[hsl(220,15%,12%)] transition-colors">
          <div className="flex items-start gap-4">
            {/* Content */}
            <div className="flex-1 min-w-0">
              {/* Badges */}
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <Badge variant="outline" className="text-gray-300 border-gray-600 text-xs">
                  {getNoteTypeLabel(note.note_type)}
                </Badge>
                {note.trade && (
                  <Badge variant="secondary" className="capitalize text-xs">
                    {note.trade}
                  </Badge>
                )}
                {note.jurisdiction_name && (
                  <span className="text-xs text-gray-500 flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {note.jurisdiction_name}
                  </span>
                )}
                {note.flag_count > 0 && (
                  <Badge className="bg-amber-600 text-xs">
                    <Flag className="h-3 w-3 mr-1" />
                    {note.flag_count}
                  </Badge>
                )}
              </div>

              {/* Content preview */}
              <p className="text-white text-sm line-clamp-2">{note.content}</p>

              {/* Footer info */}
              <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                <span className={getQualityColor(note.quality_score)}>
                  Quality: {note.quality_score ?? 'N/A'}
                </span>
                <span>Rep: {note.contributor_reputation?.toFixed(0) || '50'}</span>
                <span>{formatDate(note.created_at)}</span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onView(note)}
                className="text-gray-400 hover:text-white"
              >
                <Eye className="h-4 w-4" />
              </Button>
              {!isFlagged && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onFlag(note.id)}
                  className="text-amber-400 hover:text-amber-300 hover:bg-amber-600/20"
                >
                  <Flag className="h-4 w-4" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onReject(note)}
                className="text-red-400 hover:text-red-300 hover:bg-red-600/20"
              >
                <XCircle className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onApprove(note.id)}
                className="text-green-400 hover:text-green-300 hover:bg-green-600/20"
              >
                <CheckCircle className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </AdminCard>
      ))}
    </div>
  );
}
